/**
 * The curated catalogue.
 *
 * The stock CIRCAD libraries are a 1990s parts bin: nineteen files, 3,994
 * entries, and 59% of that is the 74-series repeated once per logic family.
 * The geometry in them is mostly good. What is bad is the *catalogue* — the
 * names, the duplication, and the fact that a modern builder searching for
 * "0805" or "SOIC-8" finds nothing, because the parts are called `SM0805` and
 * `SO8`.
 *
 * So this is a catalogue and not a drawing. Each entry names an industry
 * package and points at geometry that already exists; the ingest re-publishes
 * that geometry under the new name, keeping the old one as an alias so old
 * designs and old habits still resolve. Nothing is redrawn, nothing is
 * duplicated on disk, and a curated entry can never disagree with the part it
 * came from.
 *
 * The old libraries stay, marked `legacy`, so every design that references
 * them keeps opening — they are simply out of the way of somebody starting a
 * new board.
 *
 * `pins` and `family` are here for the footprint suggester: given a symbol
 * with fourteen pins and `DIP` in its name, the candidates are the fourteen-pin
 * through-hole packages, best first.
 */

/** What kind of thing a curated library holds. */
export interface CuratedLibrary {
  /** The name it is published under. */
  name: string;
  title: string;
  kind: 'sch' | 'pcb';
  /** Shown before the legacy libraries in every list. */
  order: number;
}

export interface CuratedEntry {
  /** The library this belongs to. */
  library: string;
  /** The industry name, and what it is published as. */
  name: string;
  /** `LIBRARY:PART` in the stock catalogue, whose geometry this republishes. */
  from: string;
  description: string;
  category: string;
  /**
   * Other names that should find this entry — the CIRCAD name it came from is
   * added automatically, so these are the extras people actually type.
   */
  aliases?: string[];
  /** Pad count, for the footprint suggester. Footprints only. */
  pins?: number;
  /** Package family, for the suggester: `DIP`, `SOIC`, `chip`, `TO`, … */
  family?: string;
  /** Surface mount rather than through hole. Footprints only. */
  smt?: boolean;
}

export const CURATED_LIBRARIES: CuratedLibrary[] = [
  { name: 'PKG-SMD', title: 'Surface-mount packages', kind: 'pcb', order: 1 },
  { name: 'PKG-THD', title: 'Through-hole packages', kind: 'pcb', order: 2 },
];

/** A chip package, both land patterns: resistors and capacitors differ. */
const chip = (
  size: string, resistor: string, capacitor: string | null, note: string,
): CuratedEntry[] => {
  const out: CuratedEntry[] = [{
    library: 'PKG-SMD', name: `R${size}`, from: `SMD:${resistor}`,
    description: `${note} chip resistor`, category: 'Chip passives',
    aliases: [size, `RES${size}`, `R-${size}`], pins: 2, family: 'chip', smt: true,
  }];
  if (capacitor) {
    out.push({
      library: 'PKG-SMD', name: `C${size}`, from: `SMD:${capacitor}`,
      description: `${note} chip capacitor`, category: 'Chip passives',
      aliases: [`CAP${size}`, `C-${size}`], pins: 2, family: 'chip', smt: true,
    });
  }
  return out;
};

/** A package that maps one-for-one onto a stock footprint. */
const pkg = (
  library: string, name: string, from: string, description: string,
  category: string, pins: number, family: string, smt: boolean,
  aliases: string[] = [],
): CuratedEntry =>
  ({ library, name, from, description, category, aliases, pins, family, smt });

const smd = (
  name: string, from: string, description: string, category: string,
  pins: number, family: string, aliases: string[] = [],
): CuratedEntry => pkg('PKG-SMD', name, `SMD:${from}`, description, category, pins, family, true, aliases);

const thd = (
  name: string, from: string, description: string, category: string,
  pins: number, family: string, aliases: string[] = [],
): CuratedEntry => pkg('PKG-THD', name, from, description, category, pins, family, false, aliases);

export const CURATED_ENTRIES: CuratedEntry[] = [
  // --- Chip passives, the two-pad workhorses ------------------------------
  ...chip('0402', 'SM0402', 'SMC0402', '1.0 × 0.5 mm'),
  ...chip('0603', 'SM0603', 'SMC0603', '1.6 × 0.8 mm'),
  ...chip('0805', 'SM0805', 'SMC0805', '2.0 × 1.25 mm'),
  ...chip('1206', 'SM1206', 'SMC1206', '3.2 × 1.6 mm'),
  ...chip('1210', 'SM1210', 'SMC1210', '3.2 × 2.5 mm'),
  ...chip('2010', 'SM2010', null, '5.0 × 2.5 mm'),
  ...chip('2512', 'SM2512', null, '6.3 × 3.2 mm'),
  ...chip('1812', 'SM1008', 'SMC1812', '4.5 × 3.2 mm'),

  // --- Small-outline transistors ------------------------------------------
  smd('SOT-23', 'SOT23', '3-lead small outline transistor', 'Transistor', 3, 'SOT', ['SOT23', 'TO-236']),
  smd('SOT-23-5', 'SOT23-5', '5-lead small outline transistor', 'Transistor', 5, 'SOT', ['SOT23-5', 'SOT-753']),
  smd('SOT-143', 'SOT143', '4-lead small outline transistor', 'Transistor', 4, 'SOT', ['SOT143']),
  smd('SOT-223', 'SOT223', '4-lead power small outline', 'Transistor', 4, 'SOT', ['SOT223']),
  smd('SOT-323', 'SOT323', 'SC-70, 3-lead', 'Transistor', 3, 'SOT', ['SOT323', 'SC-70']),
  smd('DPAK', 'DPAK', 'TO-252 power package', 'Transistor', 3, 'PAK', ['TO-252']),
  smd('D2PAK', 'D2PAK', 'TO-263 power package', 'Transistor', 3, 'PAK', ['TO-263']),

  // --- Diodes --------------------------------------------------------------
  smd('SOD-80', 'SOD80', 'MELF glass diode', 'Diode', 2, 'SOD', ['SOD80', 'MELF']),
  smd('SOD-123', 'SOD123', 'Small outline diode', 'Diode', 2, 'SOD', ['SOD123']),
  smd('DO-214', 'DO214', 'Surface-mount rectifier', 'Diode', 2, 'DO', ['DO214']),
  smd('SMA', 'SMA', 'DO-214AC rectifier', 'Diode', 2, 'DO', ['DO-214AC']),
  smd('SMB', 'SMB', 'DO-214AA rectifier', 'Diode', 2, 'DO', ['DO-214AA']),
  smd('SMC', 'SMC', 'DO-214AB rectifier', 'Diode', 2, 'DO', ['DO-214AB']),

  // --- Small-outline integrated circuits ------------------------------------
  smd('SOIC-8', 'SO8', '8-lead small outline, 150 mil', 'SOIC', 8, 'SOIC', ['SO8', 'SO-8']),
  smd('SOIC-14', 'SO14', '14-lead small outline, 150 mil', 'SOIC', 14, 'SOIC', ['SO14', 'SO-14']),
  smd('SOIC-16', 'SO16', '16-lead small outline, 150 mil', 'SOIC', 16, 'SOIC', ['SO16', 'SO-16']),
  smd('SOIC-16W', 'SOL16', '16-lead small outline, 300 mil', 'SOIC', 16, 'SOIC', ['SOL16']),
  smd('SOIC-18', 'SO18', '18-lead small outline, 150 mil', 'SOIC', 18, 'SOIC', ['SO18']),
  smd('SOIC-18W', 'SOL18', '18-lead small outline, 300 mil', 'SOIC', 18, 'SOIC', ['SOL18']),
  smd('SOIC-20W', 'SOL20', '20-lead small outline, 300 mil', 'SOIC', 20, 'SOIC', ['SOL20']),
  smd('SOIC-24W', 'SOL24', '24-lead small outline, 300 mil', 'SOIC', 24, 'SOIC', ['SOL24']),
  smd('SOIC-28W', 'SOL28', '28-lead small outline, 300 mil', 'SOIC', 28, 'SOIC', ['SOL28']),
  smd('SOJ-28', 'SOJ28', '28-lead J-lead outline', 'SOIC', 28, 'SOJ', ['SOJ28']),

  // --- Thin and shrink small outline ----------------------------------------
  smd('TSSOP-14', 'TSSOP14', '14-lead thin shrink small outline', 'TSSOP', 14, 'TSSOP', ['TSSOP14']),
  smd('TSSOP-16', 'TSSOP16', '16-lead thin shrink small outline', 'TSSOP', 16, 'TSSOP', ['TSSOP16']),
  smd('TSSOP-20', 'TSSOP20', '20-lead thin shrink small outline', 'TSSOP', 20, 'TSSOP', ['TSSOP20']),
  smd('TSSOP-28', 'TSSOP28', '28-lead thin shrink small outline', 'TSSOP', 28, 'TSSOP', ['TSSOP28']),
  smd('SSOP-48', 'SSOP48', '48-lead shrink small outline', 'TSSOP', 48, 'SSOP', ['SSOP48']),
  smd('SSOP-56', 'SSOP56', '56-lead shrink small outline', 'TSSOP', 56, 'SSOP', ['SSOP56']),

  // --- Leaded and quad flat packs -------------------------------------------
  smd('PLCC-20', 'PLCC20', '20-lead plastic leaded chip carrier', 'PLCC', 20, 'PLCC', ['PLCC20']),
  smd('PLCC-28', 'PLCC28', '28-lead plastic leaded chip carrier', 'PLCC', 28, 'PLCC', ['PLCC28']),
  smd('PLCC-32', 'PLCC32', '32-lead plastic leaded chip carrier', 'PLCC', 32, 'PLCC', ['PLCC32']),
  smd('PLCC-44', 'PLCC44', '44-lead plastic leaded chip carrier', 'PLCC', 44, 'PLCC', ['PLCC44']),
  smd('PLCC-52', 'PLCC52', '52-lead plastic leaded chip carrier', 'PLCC', 52, 'PLCC', ['PLCC52']),
  smd('PLCC-68', 'PLCC68', '68-lead plastic leaded chip carrier', 'PLCC', 68, 'PLCC', ['PLCC68']),
  smd('PLCC-84', 'PLCC84', '84-lead plastic leaded chip carrier', 'PLCC', 84, 'PLCC', ['PLCC84']),
  smd('QFP-44', 'PQFP44', '44-lead quad flat pack', 'QFP', 44, 'QFP', ['PQFP44', 'TQFP-44']),
  smd('QFP-56', 'PQFP56', '56-lead quad flat pack', 'QFP', 56, 'QFP', ['PQFP56']),
  smd('QFP-64', 'PQFP64', '64-lead quad flat pack', 'QFP', 64, 'QFP', ['PQFP64', 'TQFP-64']),
  smd('QFP-100', 'PQFP100', '100-lead quad flat pack', 'QFP', 100, 'QFP', ['PQFP100']),
  smd('QFP-128', 'PQFP128', '128-lead quad flat pack', 'QFP', 128, 'QFP', ['PQFP128']),

  // --- Dual in-line, the through-hole staple --------------------------------
  thd('DIP-6', 'THD:DIP6', '6-pin dual in-line, 300 mil', 'DIP', 6, 'DIP', ['DIP6']),
  thd('DIP-8', 'THD:DIP8', '8-pin dual in-line, 300 mil', 'DIP', 8, 'DIP', ['DIP8']),
  thd('DIP-14', 'THD:DIP14', '14-pin dual in-line, 300 mil', 'DIP', 14, 'DIP', ['DIP14']),
  thd('DIP-16', 'THD:DIP16', '16-pin dual in-line, 300 mil', 'DIP', 16, 'DIP', ['DIP16']),
  thd('DIP-18', 'THD:DIP18', '18-pin dual in-line, 300 mil', 'DIP', 18, 'DIP', ['DIP18']),
  thd('DIP-20', 'THD:DIP20', '20-pin dual in-line, 300 mil', 'DIP', 20, 'DIP', ['DIP20']),
  thd('DIP-24', 'THD:DIP24', '24-pin dual in-line, 600 mil', 'DIP', 24, 'DIP', ['DIP24']),
  thd('DIP-24N', 'THD:DIP24S', '24-pin dual in-line, 300 mil skinny', 'DIP', 24, 'DIP', ['DIP24S']),
  thd('DIP-28', 'THD:DIP28', '28-pin dual in-line, 600 mil', 'DIP', 28, 'DIP', ['DIP28']),
  thd('DIP-40', 'THD:DIP40', '40-pin dual in-line, 600 mil', 'DIP', 40, 'DIP', ['DIP40']),

  // --- Single in-line -------------------------------------------------------
  thd('SIP-2', 'THD:SIP2', '2-pin single in-line', 'SIP', 2, 'SIP', ['SIP2']),
  thd('SIP-3', 'THD:SIP3', '3-pin single in-line', 'SIP', 3, 'SIP', ['SIP3']),
  thd('SIP-4', 'THD:SIP4', '4-pin single in-line', 'SIP', 4, 'SIP', ['SIP4']),
  thd('SIP-5', 'THD:SIP5', '5-pin single in-line', 'SIP', 5, 'SIP', ['SIP5']),
  thd('SIP-6', 'THD:SIP6', '6-pin single in-line', 'SIP', 6, 'SIP', ['SIP6']),
  thd('SIP-7', 'THD:SIP7', '7-pin single in-line', 'SIP', 7, 'SIP', ['SIP7']),
  thd('SIP-8', 'THD:SIP8', '8-pin single in-line', 'SIP', 8, 'SIP', ['SIP8']),
  thd('SIP-9', 'THD:SIP9', '9-pin single in-line', 'SIP', 9, 'SIP', ['SIP9']),
  thd('SIP-10', 'THD:SIP10', '10-pin single in-line', 'SIP', 10, 'SIP', ['SIP10']),

  // --- Leaded transistors and regulators ------------------------------------
  thd('TO-92', 'THD:TO92', '3-lead plastic, 100 mil', 'Transistor', 3, 'TO', ['TO92']),
  thd('TO-220', 'THD:TO220', '3-lead power tab', 'Transistor', 3, 'TO', ['TO220']),
  thd('TO-126', 'THD:TO126', '3-lead medium power', 'Transistor', 3, 'TO', ['TO126']),
  thd('TO-39', 'THD:TO39', '3-lead metal can', 'Transistor', 3, 'TO', ['TO39']),
  thd('TO-3', 'THD:TO3', '2-lead power can', 'Transistor', 2, 'TO', ['TO3']),
  thd('TO-218', 'THD:TO218F', '3-lead power tab, isolated', 'Transistor', 3, 'TO', ['TO218F']),

  // --- Leaded passives ------------------------------------------------------
  thd('RES-300mil', 'THD:R300P60', 'Axial resistor, 0.3" body, 0.6" pitch', 'Leaded passives', 2, 'axial', ['R300P60', '1/8W']),
  thd('RES-400mil', 'THD:R400P70', 'Axial resistor, 0.4" body, 0.7" pitch — quarter watt', 'Leaded passives', 2, 'axial', ['R400P70', '1/4W']),
  thd('RES-500mil', 'THD:R500P70', 'Axial resistor, 0.5" body, 0.7" pitch', 'Leaded passives', 2, 'axial', ['R500P70', '1/2W']),
  thd('RES-600mil', 'THD:R600P80', 'Axial resistor, 0.6" body, 0.8" pitch', 'Leaded passives', 2, 'axial', ['R600P80', '1W']),
  thd('RES-800mil', 'THD:R800P100', 'Axial resistor, 0.8" body, 1.0" pitch', 'Leaded passives', 2, 'axial', ['R800P100', '2W']),
  thd('CAP-DISC-100mil', 'THD:CD100P60', 'Disc capacitor, 0.1" lead spacing', 'Leaded passives', 2, 'radial', ['CD100P60']),
  thd('CAP-DISC-200mil', 'THD:CD200P60', 'Disc capacitor, 0.2" lead spacing', 'Leaded passives', 2, 'radial', ['CD200P60']),
  thd('CAP-DISC-300mil', 'THD:CD300P70', 'Disc capacitor, 0.3" lead spacing', 'Leaded passives', 2, 'radial', ['CD300P70']),
  thd('CAP-BOX-300mil', 'THD:CB300P70', 'Box capacitor, 0.3" lead spacing', 'Leaded passives', 2, 'radial', ['CB300P70']),
  thd('CAP-BOX-400mil', 'THD:CB400P70', 'Box capacitor, 0.4" lead spacing', 'Leaded passives', 2, 'radial', ['CB400P70']),
  thd('DIODE-300mil', 'THD:D300P70', 'Axial diode, 0.3" body', 'Diode', 2, 'axial', ['D300P70', 'DO-35']),
  thd('DIODE-400mil', 'THD:D400P70', 'Axial diode, 0.4" body', 'Diode', 2, 'axial', ['D400P70', 'DO-41']),
  thd('LED-3mm', 'THD:LED', 'Light emitting diode, 0.1" lead spacing', 'Diode', 2, 'LED', ['LED']),

  // --- Connectors -----------------------------------------------------------
  thd('IDC-10', 'THD:IDC10', '10-way boxed header, 2 rows', 'Connector', 10, 'IDC', ['IDC10']),
  thd('IDC-14', 'THD:IDC14', '14-way boxed header, 2 rows', 'Connector', 14, 'IDC', ['IDC14']),
  thd('IDC-16', 'THD:IDC16', '16-way boxed header, 2 rows', 'Connector', 16, 'IDC', ['IDC16']),
  thd('IDC-20', 'THD:IDC20', '20-way boxed header, 2 rows', 'Connector', 20, 'IDC', ['IDC20']),
  thd('IDC-26', 'THD:IDC26', '26-way boxed header, 2 rows', 'Connector', 26, 'IDC', ['IDC26']),
  thd('IDC-34', 'THD:IDC34', '34-way boxed header, 2 rows', 'Connector', 34, 'IDC', ['IDC34']),
  thd('IDC-40', 'THD:IDC40', '40-way boxed header, 2 rows', 'Connector', 40, 'IDC', ['IDC40']),
  thd('IDC-50', 'THD:IDC50', '50-way boxed header, 2 rows', 'Connector', 50, 'IDC', ['IDC50']),
  thd('DB9-M', 'THD:DB9M', '9-way D connector, male', 'Connector', 9, 'D-sub', ['DB9M']),
  thd('DB9-F', 'THD:DB9F', '9-way D connector, female', 'Connector', 9, 'D-sub', ['DB9F']),
  thd('DB15-M', 'THD:DB15M', '15-way D connector, male', 'Connector', 15, 'D-sub', ['DB15M']),
  thd('DB15-F', 'THD:DB15F', '15-way D connector, female', 'Connector', 15, 'D-sub', ['DB15F']),
  thd('DB25-M', 'THD:DB25M', '25-way D connector, male', 'Connector', 25, 'D-sub', ['DB25M']),
  thd('DB25-F', 'THD:DB25F', '25-way D connector, female', 'Connector', 25, 'D-sub', ['DB25F']),
];

/** Libraries that are kept for the designs that reference them, and no longer led with. */
export const LEGACY_LIBRARIES = new Set([
  'ANALOG', 'CMOS', 'DISCRETE', 'ECL', 'MEMORY', 'Micro', 'PCB', 'PLD', 'SCH',
  'SMD', 'THD', 'TTL', 'TTL-als', 'TTL-as', 'TTL-f', 'TTL-hc', 'TTL-hct',
  'TTL-ls', 'TTL-s',
]);

/** `LIBRARY:PART` split, for reading an entry's `from`. */
export function splitFrom(from: string): { library: string; part: string } {
  const i = from.indexOf(':');
  return i < 0
    ? { library: '', part: from }
    : { library: from.slice(0, i), part: from.slice(i + 1) };
}

/**
 * Every name a curated entry answers to, pointing at the stock part it
 * republishes.
 *
 * The catalogue is a rename, and a rename has to work in both directions or it
 * is a break. A design saved today names `DIP-8`; one saved in 1998 names
 * `DIP8`; the same geometry answers both. This is also what lets the curated
 * names work before the catalogue has been ingested anywhere — the development
 * server parses the stock `.LIB` files directly and has never heard of
 * `PKG-THD`, so without this the names it offers would resolve to nothing there.
 */
const BY_NAME = new Map<string, CuratedEntry>();
for (const e of CURATED_ENTRIES) {
  BY_NAME.set(e.name.toUpperCase(), e);
  const { part } = splitFrom(e.from);
  if (!BY_NAME.has(part.toUpperCase())) BY_NAME.set(part.toUpperCase(), e);
  for (const a of e.aliases ?? []) {
    if (!BY_NAME.has(a.toUpperCase())) BY_NAME.set(a.toUpperCase(), e);
  }
}

/** The curated entry a name refers to, under any of its names. */
export function curatedEntry(name: string): CuratedEntry | null {
  return BY_NAME.get(name.trim().toUpperCase()) ?? null;
}

/**
 * Other names worth trying when a footprint name does not resolve.
 *
 * In order: the curated name, the stock name it came from, then its aliases.
 * Never includes the name asked for.
 */
export function footprintAliases(name: string): string[] {
  const e = curatedEntry(name);
  if (!e) return [];
  const { part } = splitFrom(e.from);
  const seen = new Set([name.trim().toUpperCase()]);
  const out: string[] = [];
  for (const n of [e.name, part, ...(e.aliases ?? [])]) {
    const k = n.toUpperCase();
    if (seen.has(k)) continue;
    seen.add(k);
    out.push(n);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Drawn rather than collected
// ---------------------------------------------------------------------------

/**
 * A footprint the catalogue draws because nothing in the stock libraries has it.
 *
 * Only the three parametric families. Anything with a shape to it — a TO-220
 * tab, a connector shell — is collected, not invented, because getting those
 * right needs a drawing and not arithmetic.
 */
export interface GeneratedEntry {
  library: string;
  name: string;
  description: string;
  category: string;
  aliases: string[];
  pins: number;
  family: string;
  smt: boolean;
  /** What to build: the family, and the numbers it needs. */
  make: { kind: 'dil'; pins: number }
  | { kind: 'sil'; pins: number }
  | { kind: 'edge'; fingers: number; pitch: number; sides: 1 | 2 };
}

/** DIL sizes the stock libraries already carry, republished rather than drawn. */
const DIL_IN_STOCK = new Set([6, 8, 14, 16, 18, 20, 24, 28, 40]);
/** Every DIL size worth having. 4 is an optocoupler; 64 is a 68000. */
const DIL_SIZES = [4, 6, 8, 10, 12, 14, 16, 18, 20, 22, 24, 28, 32, 36, 40, 42, 48, 52, 64];

const SIL_IN_STOCK = new Set([2, 3, 4, 5, 6, 7, 8, 9, 10]);
/** Headers run to forty ways; a resistor network rarely past ten. */
const SIL_SIZES = [
  2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20,
  22, 24, 26, 28, 30, 32, 36, 40,
];

const dil = (pins: number): GeneratedEntry => ({
  library: 'PKG-THD', name: `DIP-${pins}`,
  description: `${pins}-pin dual in-line, ${Math.round(dilWidthOf(pins) * 1000)} mil`,
  category: 'DIP', aliases: [`DIP${pins}`, `DIL-${pins}`, `DIL${pins}`],
  pins, family: 'DIP', smt: false, make: { kind: 'dil', pins },
});

const sil = (pins: number): GeneratedEntry => ({
  library: 'PKG-THD', name: `SIP-${pins}`,
  description: `${pins}-pin single in-line, 100 mil pitch`,
  category: 'SIP', aliases: [`SIP${pins}`, `SIL-${pins}`, `SIL${pins}`, `HDR-${pins}`],
  pins, family: 'SIP', smt: false, make: { kind: 'sil', pins },
});

const edge = (
  fingers: number, pitch: number, sides: 1 | 2, note: string,
): GeneratedEntry => {
  const name = `EDGE-${fingers}${sides === 2 ? 'x2' : ''}-${String(pitch).replace('0.', '')}`;
  return {
    library: 'PKG-THD', name,
    description: `${fingers}-way card edge, ${pitch}" pitch${sides === 2 ? ', both sides' : ''} — ${note}`,
    category: 'Card edge',
    aliases: [`EDGE${fingers}`, `CARD-${fingers}`, `FINGERS-${fingers}`],
    pins: fingers * sides, family: 'edge', smt: true,
    make: { kind: 'edge', fingers, pitch, sides },
  };
};

/** Kept in step with `dilFootprint`'s own rule, which is the one that counts. */
function dilWidthOf(pins: number): number {
  if (pins <= 20) return 0.3;
  return pins >= 60 ? 0.9 : 0.6;
}

export const GENERATED_ENTRIES: GeneratedEntry[] = [
  ...DIL_SIZES.filter((n) => !DIL_IN_STOCK.has(n)).map(dil),
  ...SIL_SIZES.filter((n) => !SIL_IN_STOCK.has(n)).map(sil),
  // The pitches that actually exist on cards: 0.1" for the old bus boards,
  // 0.156" for power and backplane edges, 0.05" for anything modern.
  edge(10, 0.1, 1, 'small expansion card'),
  edge(20, 0.1, 1, 'expansion card'),
  edge(22, 0.156, 1, 'S-100 / power edge'),
  edge(30, 0.1, 2, 'double-sided bus card'),
  edge(36, 0.1, 2, 'ISA-width bus card'),
  edge(40, 0.05, 2, 'fine-pitch card edge'),
  edge(44, 0.156, 2, 'heavy power edge'),
];

// ---------------------------------------------------------------------------
// Symbols
// ---------------------------------------------------------------------------

/**
 * How a curated symbol library is built out of a stock one.
 *
 * The eight TTL libraries are the same 297 logic functions eight times over:
 * `7404`, `LS04`, `HC04`, `HCT04`, `S04`, `F04`, `AS04` and `ALS04` are one
 * hex inverter with one set of pin numbers. Their symbols differ in exactly
 * one respect — the LS family draws a longer leg on its power pins — and
 * nothing electrical differs at all.
 *
 * So the catalogue keeps one entry per *function* and makes every family
 * spelling find it. That is not a loss of coverage, it is a gain: the stock
 * libraries answer to `LS04` and nothing else, and nobody has typed that since
 * about 1994. `74LS04` finds it now, and so does `LS04`.
 */
export interface SymbolRule {
  /** The library to publish. */
  library: string;
  title: string;
  /** The stock library whose symbols and part list are used. */
  from: string;
  /**
   * The part number a canonical name is built from, as a prefix that is
   * stripped to leave the function: `74` turns `7404` into `04`.
   */
  strip: RegExp;
  /** Family spellings, each of which becomes two aliases: `LS04` and `74LS04`. */
  families: string[];
  /** Whole-name prefixes that are also worth answering to: `CD4011`. */
  makers?: string[];
}

export const SYMBOL_RULES: SymbolRule[] = [
  {
    library: 'LOGIC-74',
    title: '74-series logic',
    from: 'TTL',
    strip: /^74/,
    families: ['LS', 'HC', 'HCT', 'S', 'F', 'AS', 'ALS'],
    makers: ['SN74', 'DM74', 'MC74'],
  },
  {
    library: 'LOGIC-4000',
    title: '4000-series CMOS',
    from: 'CMOS',
    strip: /^(?!)/,          // the 4000 numbers are already canonical
    families: [],
    // `MC1` and not `MC14`: Motorola's part is MC14011, which is the prefix
    // followed by the whole number. `MC14` would have built `MC144011`.
    makers: ['CD', 'MC1', 'HEF', 'HCF', 'TC'],
  },
];

/** Every name a curated symbol should answer to. */
export function symbolAliases(name: string, rule: SymbolRule): string[] {
  const fn = name.replace(rule.strip, '');
  const out = new Set<string>();
  for (const f of rule.families) {
    out.add(`${f}${fn}`);          // LS04 — what the stock library calls it
    out.add(`74${f}${fn}`);        // 74LS04 — what anybody would type
  }
  for (const m of rule.makers ?? []) out.add(`${m}${fn}`);
  out.delete(name.toUpperCase());
  out.delete(name);
  return [...out];
}
